import {
  describeDistribution,
  distributionSegments,
  STATUS_COUNT_LABEL,
  STATUS_FILL,
} from './evidence';

import { cn } from '@/lib/cn';
import type { ReadinessEvidenceCounts } from '@/lib/api/readiness';

export interface EvidenceDistributionProps {
  counts?: ReadinessEvidenceCounts | null;
  /** Show the count chips under the bar. Off inside tight rows. */
  chips?: boolean;
  /** Renders an inert track — for the moment before the evidence lands. */
  loading?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * The shape of a component's evidence: how much of it is costing score, how
 * much is counted, in one bar.
 *
 * This is the thing that had to replace the flat list. A 327-topic curriculum
 * cannot be read as rows, but it can be read as a proportion in a single
 * glance — and because it is drawn from `counts` rather than from `items`, its
 * height is the same at three records and at three thousand.
 *
 * The bar is a single labelled image rather than a set of announced segments:
 * a screen reader gets the whole sentence once ("327 records — 64 costing
 * score, 262 counted in full") instead of four fragments. The visible chips
 * repeat that sentence, so they are hidden from assistive tech to avoid saying
 * it twice.
 */
export function EvidenceDistribution({
  counts,
  chips = true,
  loading = false,
  size = 'sm',
  className,
}: EvidenceDistributionProps) {
  const segments = distributionSegments(counts ?? undefined);
  const height = size === 'md' ? 'h-2' : 'h-1.5';

  if (segments.length === 0) {
    if (!loading) return null;
    return (
      <div className={className}>
        <div
          className={cn('w-full overflow-hidden rounded-full bg-muted', height)}
          aria-hidden="true"
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        role="img"
        aria-label={describeDistribution(counts ?? undefined)}
        className={cn('flex w-full overflow-hidden rounded-full bg-muted', height)}
      >
        {segments.map((segment) => (
          <span
            key={segment.status}
            className={cn('h-full', STATUS_FILL[segment.status])}
            // Sub-percent slivers would vanish entirely; a hairline is still a
            // signal that the status exists at all.
            style={{ width: `${Math.max(segment.pct, 1.5)}%` }}
          />
        ))}
      </div>
      {chips && (
        <p
          className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10.5px] leading-tight text-muted-foreground"
          aria-hidden="true"
        >
          {segments.map((segment, i) => (
            <span key={segment.status} className="whitespace-nowrap">
              {i > 0 && <span className="mr-1.5 text-muted-foreground/50">·</span>}
              <span className="font-semibold tabular-nums text-foreground">{segment.count}</span>
              {` ${STATUS_COUNT_LABEL[segment.status]}`}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
